import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import { AutoComplete, Checkbox, Col, Form, Input, InputNumber, Row, Select, Space, Tag, Tooltip } from "antd";
import { useMemo } from "react";
import { TRANSLATIONS_PROPERTY_NAME } from "@devowl-wp/api";
import { DEFAULT_LANGUAGE, ETechnicalDefinitionHostType, ETechnicalDefinitionType, EUserRole, ServiceTemplateField } from "@devowl-wp/api-real-cookie-banner";
import { getServiceTemplateTechnicalDefinitionTypes, useTranslateTypeKey } from "./useDefinitionTypes.js";
import { useServiceTemplateTechnicalDefinitionDurationTypeKeys, useTranslateDurationTypeKey } from "./useDurationUnitTypes.js";
import { useServiceTemplateTechnicalDefinitionHostKeys, useTranslateServiceTemplateTechnicalDefinitionHostKeySample } from "./useHostTypes.js";
import { isValidHostByType } from "./util.js";
import { useStores } from "../../../../../../../store/root.js";
import { ETechnicalDefinitionBadgeType } from "../../../../../../../types/technical-definitions/cookies.js";
import { mergeStyles } from "../../../../../../../util/style.js";
import { getTranslationFlagElementStyle, hasTranslationFlagsChanged, onTranslationValueChange } from "../../../../../../../util/translation.js";
import { ColumnToolTipHeader } from "../../../../../../table/column-tooltip-header.js";
import { LanguageIcon } from "../../../fields/translation/language-icon-addon/language-icon.js";
import { TranslationTextAreaFormFields } from "../../../fields/translation/language-text-area-input/language-text-area-form-fields.js";
/**
 * Creates a normalized key for technical definition comparison.
 * This function ensures consistent comparison by normalizing the data.
 */ function createTechnicalDefinitionKey(definition) {
    if (!definition) {
        return "";
    }
    // Normalize and trim all values, convert to lowercase for case-insensitive comparison
    const type = (definition.type || "").toString().trim().toLowerCase();
    const name = (definition.name || "").toString().trim().toLowerCase();
    const host = (definition.host || "").toString().trim().toLowerCase();
    // Create a consistent key format
    return `${type}|${name}|${host}`;
}
/**
 * Returns all duplicates of technical definitions by name, type, and host.
 */ function getTechnicalDefinitionDuplicates(technicalDefinitions = []) {
    // We will check for duplicates by name, type, and host
    const keys = technicalDefinitions.map((definition)=>{
        return createTechnicalDefinitionKey(definition);
    });
    // As result we want to have all keys which are duplicated
    const duplicates = keys.filter((key, index)=>keys.indexOf(key) !== index);
    return duplicates;
}
function useServiceTemplateTechnicalDefinitionsTableColumns(t) {
    const { rcbAuthenticationStore } = useStores();
    const form = Form.useFormInstance();
    const hasCreatorRole = rcbAuthenticationStore.hasRole(EUserRole.Creator);
    const hostTypeOptions = useMemo(()=>{
        return useServiceTemplateTechnicalDefinitionHostKeys().map((key)=>{
            return {
                value: key,
                label: /*#__PURE__*/ _jsxs("div", {
                    style: {
                        display: "flex",
                        justifyContent: "space-between"
                    },
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            style: {
                                minWidth: "1rem"
                            },
                            children: key
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            style: {
                                minWidth: "3rem"
                            }
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            children: /*#__PURE__*/ _jsxs("i", {
                                children: [
                                    t("e.g. "),
                                    /*#__PURE__*/ _jsx("b", {
                                        children: useTranslateServiceTemplateTechnicalDefinitionHostKeySample(t, key)
                                    })
                                ]
                            })
                        })
                    ]
                })
            };
        });
    }, []);
    return [
        {
            title: t("Cookie Type"),
            dataIndex: "type",
            render: (_, record)=>{
                const options = getServiceTemplateTechnicalDefinitionTypes(t);
                return /*#__PURE__*/ _createElement(Form.Item, {
                    ...record,
                    key: "type",
                    name: [
                        record.name,
                        "type"
                    ]
                }, /*#__PURE__*/ _jsx(Select, {
                    placeholder: "Select Type",
                    optionFilterProp: "children",
                    options: options,
                    disabled: !hasCreatorRole
                }));
            }
        },
        {
            title: /*#__PURE__*/ _jsx(ColumnToolTipHeader, {
                columnName: t("Technical Cookie Name"),
                tooltipTitle: t("Every cookie has a technical name, which you must provide. If a cookie name is dynamically composed, please use an asterisk (*) as a wildcard (placeholder).")
            }),
            dataIndex: "name",
            render: (_, record, index)=>/*#__PURE__*/ _jsx(_Fragment, {
                    children: /*#__PURE__*/ _jsx(Form.Item, {
                        noStyle: true,
                        shouldUpdate: (prev, next)=>prev.technicalDefinitions[record.name]?.name !== next.technicalDefinitions[record.name]?.name,
                        children: ()=>{
                            return /*#__PURE__*/ _createElement(Form.Item, {
                                ...record,
                                key: "name",
                                name: [
                                    record.name,
                                    "name"
                                ],
                                rules: [
                                    ({ getFieldValue })=>({
                                            validator: ()=>{
                                                const technicalDefinitions = getFieldValue(ServiceTemplateField.TechnicalDefinitions);
                                                if (!technicalDefinitions || technicalDefinitions.length === 0) {
                                                    return Promise.resolve();
                                                }
                                                const currentRow = getFieldValue([
                                                    ServiceTemplateField.TechnicalDefinitions,
                                                    record.name
                                                ]);
                                                const currentKey = createTechnicalDefinitionKey(currentRow);
                                                const duplicates = getTechnicalDefinitionDuplicates(technicalDefinitions);
                                                if (duplicates.includes(currentKey)) {
                                                    return Promise.reject(new Error(t("Please give a unique name for this field, type, host combination.")));
                                                } else {
                                                    return Promise.resolve();
                                                }
                                            }
                                        }),
                                    {
                                        required: true,
                                        message: t("Provide a technical cookie name.")
                                    }
                                ]
                            }, /*#__PURE__*/ _jsx(Input, {
                                style: {
                                    minWidth: "5rem",
                                    maxWidth: "100%"
                                },
                                disabled: !hasCreatorRole
                            }));
                        }
                    })
                })
        },
        {
            title: /*#__PURE__*/ _jsx(ColumnToolTipHeader, {
                columnName: t("Technical Cookie Host"),
                tooltipTitle: t("Every cookie is associated to a domain or hostname. If it is the hostname of website with the cookie banner, use the suggested variables.")
            }),
            dataIndex: "host",
            render: (_, record)=>/*#__PURE__*/ _jsx(_Fragment, {
                    children: /*#__PURE__*/ _jsx(Form.Item, {
                        noStyle: true,
                        shouldUpdate: (prev, next)=>prev.technicalDefinitions[record.name]?.host !== next.technicalDefinitions[record.name]?.host || prev.technicalDefinitions[record.name]?.type !== next.technicalDefinitions[record.name]?.type,
                        children: ()=>{
                            return /*#__PURE__*/ _createElement(Form.Item, {
                                ...record,
                                key: "host",
                                name: [
                                    record.name,
                                    "host"
                                ],
                                dependencies: [
                                    [
                                        ServiceTemplateField.TechnicalDefinitions,
                                        record.name,
                                        "type"
                                    ]
                                ],
                                rules: [
                                    ({ getFieldValue })=>({
                                            validator: ()=>{
                                                const currentRow = getFieldValue([
                                                    ServiceTemplateField.TechnicalDefinitions,
                                                    record.name
                                                ]);
                                                const { type, host } = currentRow;
                                                if (isValidHostByType(type, host)) {
                                                    return Promise.resolve();
                                                } else {
                                                    return Promise.reject(t("Please provide a valid hostname!"));
                                                }
                                            }
                                        }),
                                    {
                                        required: true,
                                        message: t("Provide a technical cookie host.")
                                    }
                                ]
                            }, /*#__PURE__*/ _jsx(AutoComplete, {
                                options: hostTypeOptions,
                                style: {
                                    width: "100%"
                                },
                                popupMatchSelectWidth: false,
                                disabled: !hasCreatorRole,
                                children: /*#__PURE__*/ _jsx(Input, {
                                    style: {
                                        maxWidth: "100%"
                                    },
                                    disabled: !hasCreatorRole
                                })
                            }));
                        }
                    })
                })
        },
        {
            title: /*#__PURE__*/ _jsx(ColumnToolTipHeader, {
                columnName: t("Cookie Duration"),
                tooltipTitle: t("A HTTP cookie is only valid for a certain time, which is defined when the cookie is set.")
            }),
            dataIndex: "duration",
            render: (_, record)=>/*#__PURE__*/ _jsx(_Fragment, {
                    children: /*#__PURE__*/ _jsx(Form.Item, {
                        noStyle: true,
                        shouldUpdate: (prev, next)=>{
                            const prevDefinition = prev.technicalDefinitions[record.name];
                            const nextDefinition = next.technicalDefinitions[record.name];
                            return prevDefinition?.isSessionDuration !== nextDefinition?.isSessionDuration || prevDefinition?.type !== nextDefinition?.type;
                        },
                        children: ({ getFieldValue })=>{
                            const isSessionDuration = getFieldValue([
                                ServiceTemplateField.TechnicalDefinitions,
                                record.name,
                                "isSessionDuration"
                            ]);
                            const type = getFieldValue([
                                ServiceTemplateField.TechnicalDefinitions,
                                record.name,
                                "type"
                            ]);
                            const isDurationType = [
                                ETechnicalDefinitionType.Http
                            ].findIndex((typeKey)=>typeKey === type) >= 0;
                            const options = useServiceTemplateTechnicalDefinitionDurationTypeKeys().map((typeKey)=>{
                                return {
                                    value: typeKey,
                                    label: useTranslateDurationTypeKey(t, typeKey)
                                };
                            });
                            return /*#__PURE__*/ _jsx(_Fragment, {
                                children: /*#__PURE__*/ _jsxs(Row, {
                                    hidden: !isDurationType,
                                    children: [
                                        /*#__PURE__*/ _jsx(Col, {
                                            children: /*#__PURE__*/ _jsx(Tooltip, {
                                                title: t("This cookie is active as long as the session is active"),
                                                children: /*#__PURE__*/ _createElement(Form.Item, {
                                                    ...record,
                                                    key: "isSessionDuration",
                                                    name: [
                                                        record.name,
                                                        "isSessionDuration"
                                                    ],
                                                    valuePropName: "checked",
                                                    style: {
                                                        marginBottom: "0px"
                                                    }
                                                }, /*#__PURE__*/ _jsx(Checkbox, {
                                                    disabled: !hasCreatorRole,
                                                    children: t("Session")
                                                }))
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(Col, {
                                            children: !isSessionDuration && /*#__PURE__*/ _jsxs(Space.Compact, {
                                                children: [
                                                    /*#__PURE__*/ _createElement(Form.Item, {
                                                        ...record,
                                                        key: "duration",
                                                        name: [
                                                            record.name,
                                                            "duration"
                                                        ],
                                                        rules: [
                                                            {
                                                                required: isSessionDuration,
                                                                message: t("Enter duration.")
                                                            }
                                                        ]
                                                    }, /*#__PURE__*/ _jsx(InputNumber, {
                                                        min: 0,
                                                        disabled: !hasCreatorRole
                                                    })),
                                                    /*#__PURE__*/ _createElement(Form.Item, {
                                                        ...record,
                                                        key: "durationUnit",
                                                        name: [
                                                            record.name,
                                                            "durationUnit"
                                                        ],
                                                        rules: [
                                                            {
                                                                required: isSessionDuration,
                                                                message: t("Choose unit.")
                                                            }
                                                        ]
                                                    }, /*#__PURE__*/ _jsx(Select, {
                                                        options: options,
                                                        style: {
                                                            minWidth: "5rem"
                                                        },
                                                        disabled: !hasCreatorRole
                                                    }))
                                                ]
                                            })
                                        })
                                    ]
                                })
                            });
                        }
                    })
                })
        },
        {
            title: /*#__PURE__*/ _jsx(ColumnToolTipHeader, {
                columnName: t("Cookie Purpose"),
                tooltipTitle: t("Each cookie serves a purpose (e.g. user identification for tracking), which should be explained.")
            }),
            dataIndex: "purpose",
            render: (_, record)=>/*#__PURE__*/ _jsx(_Fragment, {
                    children: /*#__PURE__*/ _jsx(Form.Item, {
                        noStyle: true,
                        shouldUpdate: (prev, next)=>hasTranslationFlagsChanged(prev, next, [
                                ServiceTemplateField.TechnicalDefinitions,
                                record.name,
                                "purpose"
                            ]),
                        children: ({ getFieldValue })=>{
                            const purposeRules = [
                                {
                                    warningOnly: true,
                                    validator: (_, value)=>{
                                        if (value && value.length > 500) {
                                            const technicalDefinition = getFieldValue(_.fullField.split(".").slice(0, 2));
                                            return Promise.reject(t(`Please try to use short and user friendly descriptions for the purpose of "{{name}}" ({{type}}, {{language}}) if possible.`, {
                                                type: useTranslateTypeKey(t, technicalDefinition.type),
                                                name: technicalDefinition.name,
                                                language: technicalDefinition.language
                                            }));
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ];
                            const relativePath = [
                                record.name,
                                "purpose"
                            ];
                            const absolutePath = [
                                ServiceTemplateField.TechnicalDefinitions,
                                record.name,
                                "purpose"
                            ];
                            const dynamicStyle = mergeStyles({
                                maxWidth: "100%"
                            }, getTranslationFlagElementStyle(form, absolutePath));
                            return /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsxs(Row, {
                                        gutter: [
                                            10,
                                            10
                                        ],
                                        style: {
                                            flexFlow: "nowrap"
                                        },
                                        children: [
                                            /*#__PURE__*/ _jsx(Col, {
                                                flex: 1,
                                                children: /*#__PURE__*/ _createElement(Form.Item, {
                                                    ...record,
                                                    key: "purpose",
                                                    name: relativePath,
                                                    rules: purposeRules,
                                                    noStyle: true
                                                }, /*#__PURE__*/ _jsx(Input.TextArea, {
                                                    style: dynamicStyle,
                                                    onChange: ()=>onTranslationValueChange(form, absolutePath),
                                                    autoSize: {
                                                        minRows: 1,
                                                        maxRows: 3
                                                    },
                                                    disabled: !hasCreatorRole
                                                }))
                                            }),
                                            /*#__PURE__*/ _jsx(Col, {
                                                flex: "0",
                                                children: /*#__PURE__*/ _jsx(LanguageIcon, {
                                                    lang: DEFAULT_LANGUAGE,
                                                    flexPathConfig: {
                                                        isFormListItem: true,
                                                        relative: {
                                                            namePath: relativePath,
                                                            namePathPrefix: ServiceTemplateField.TechnicalDefinitions,
                                                            customFlagNamePath: (lang)=>[
                                                                    ServiceTemplateField.TechnicalDefinitions,
                                                                    record.name,
                                                                    TRANSLATIONS_PROPERTY_NAME,
                                                                    lang,
                                                                    "purpose"
                                                                ],
                                                            translationNamePath: (lang)=>[
                                                                    ServiceTemplateField.TechnicalDefinitions,
                                                                    record.name,
                                                                    TRANSLATIONS_PROPERTY_NAME,
                                                                    lang,
                                                                    "purpose"
                                                                ]
                                                        }
                                                    }
                                                })
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(TranslationTextAreaFormFields, {
                                        flexPathConfig: {
                                            isFormListItem: true,
                                            relative: {
                                                namePath: (lang)=>[
                                                        record.name,
                                                        TRANSLATIONS_PROPERTY_NAME,
                                                        lang,
                                                        "purpose"
                                                    ],
                                                customFlagNamePath: (lang)=>[
                                                        ServiceTemplateField.TechnicalDefinitions,
                                                        record.name,
                                                        TRANSLATIONS_PROPERTY_NAME,
                                                        lang,
                                                        "purpose"
                                                    ]
                                            }
                                        },
                                        textAreaProps: {
                                            style: {
                                                maxWidth: "100%"
                                            },
                                            autoSize: {
                                                minRows: 1,
                                                maxRows: 3
                                            }
                                        },
                                        formProps: {
                                            rules: purposeRules
                                        }
                                    })
                                ]
                            });
                        }
                    })
                })
        },
        {
            title: t("Import status"),
            dataIndex: "status",
            render: (_, record)=>{
                return /*#__PURE__*/ _jsx(_Fragment, {
                    children: /*#__PURE__*/ _jsx(Form.Item, {
                        noStyle: true,
                        shouldUpdate: (prev, next)=>prev.technicalDefinitions[record.name]?.status !== next.technicalDefinitions[record.name]?.status,
                        children: ({ getFieldValue })=>{
                            const status = getFieldValue([
                                ServiceTemplateField.TechnicalDefinitions,
                                record.name,
                                "status"
                            ]);
                            switch(status){
                                case ETechnicalDefinitionBadgeType.Modified:
                                    return /*#__PURE__*/ _jsx(Tag, {
                                        color: "blue",
                                        children: t("Modified")
                                    });
                                case ETechnicalDefinitionBadgeType.New:
                                    return /*#__PURE__*/ _jsx(Tag, {
                                        color: "green",
                                        children: t("New")
                                    });
                                default:
                                    return null;
                            }
                        }
                    })
                });
            }
        }
    ];
}
function useServiceTemplateTechnicalDefinitionsTableColumnsReadOnly(t, hiddenColumns = []) {
    return [
        {
            title: t("#"),
            dataIndex: "orderPosition",
            render: (_)=>_ + 1,
            align: "center"
        },
        {
            title: t("Cookie Type"),
            dataIndex: "type",
            render: (_)=>/*#__PURE__*/ _jsx(Tag, {
                    color: "grey",
                    children: useTranslateTypeKey(t, _)
                })
        },
        {
            title: t("Technical Cookie Name"),
            dataIndex: "name"
        },
        {
            title: t("Technical Cookie Host"),
            dataIndex: "host",
            render: (_)=>{
                if (Object.values(ETechnicalDefinitionHostType).includes(_)) {
                    return /*#__PURE__*/ _jsx(Tag, {
                        color: "grey",
                        children: _
                    });
                }
                return _;
            }
        },
        {
            title: t("Cookie Duration"),
            dataIndex: "duration",
            render: (_, record)=>{
                const isDurationType = [
                    ETechnicalDefinitionType.Http
                ].findIndex((typeKey)=>typeKey === record.type) >= 0;
                return record.isSessionDuration ? /*#__PURE__*/ _jsx(Tag, {
                    color: "grey",
                    children: t("Session Cookie")
                }) : isDurationType ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        record.duration,
                        " ",
                        useTranslateDurationTypeKey(t, record.durationUnit)
                    ]
                }) : null;
            }
        },
        {
            title: t("Cookie Purpose"),
            dataIndex: "purpose"
        }
    ].filter((item)=>!hiddenColumns.includes(item.dataIndex));
}
export { useServiceTemplateTechnicalDefinitionsTableColumns, useServiceTemplateTechnicalDefinitionsTableColumnsReadOnly };
